import { router, usePage } from '@inertiajs/react'
import { useState, type FormEvent } from 'react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  locale: string
  loginUrl: string
  createUrl: string
  errors: Record<string, string>
  domainSuffix: string
  translations: {
    welcome: string
    pleaseRegister: string
    login: string
    createAccount: string
    email: string
    password: string
    confirmPassword: string
    subdomain: string
    createAccountBtn: string
  }
  [key: string]: unknown
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function AccountsNew() {
  const { loginUrl, createUrl, errors, domainSuffix, translations: t } =
    usePage<Props>().props
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [processing, setProcessing] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.post(
      createUrl,
      {
        account: {
          subdomain_name: subdomain,
          user_attributes: {
            email,
            password,
            password_confirmation: passwordConfirmation,
            is_admin: true,
          },
        },
      },
      { onFinish: () => setProcessing(false) },
    )
  }

  return (
    <div className="flex min-h-screen">
      {/* Left panel */}
      <div
        className="hidden w-1/4 flex-col items-center justify-center text-center text-white md:flex"
        style={{ background: 'linear-gradient(to right, #252854, #707070)' }}
      >
        <img
          src="/assets/eventx-logo-white.png"
          alt="Eventy"
          className="mb-6 w-1/2"
          onError={(e) => {
            ;(e.target as HTMLImageElement).style.display = 'none'
          }}
        />
        <h3 className="mb-3 text-2xl font-medium text-white">{t.welcome}</h3>
        <p className="px-8 text-sm text-white/80">{t.pleaseRegister}.</p>
        <a
          href={loginUrl}
          className="mt-8 rounded-full bg-white/90 px-8 py-2 text-sm font-bold text-gray-700 transition-colors hover:bg-white"
        >
          {t.login}
        </a>
      </div>

      {/* Right panel */}
      <div className="flex flex-1 items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md">
          <h3 className="mb-8 text-center text-2xl font-semibold" style={{ color: '#252854' }}>
            {t.createAccount}
          </h3>

          {/* Mobile login link */}
          <div className="mb-6 text-center md:hidden">
            <a href={loginUrl} className="text-sm font-medium" style={{ color: '#252854' }}>
              {t.login}
            </a>
          </div>

          {/* Error summary */}
          {Object.keys(errors).length > 0 && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <ul className="list-inside list-disc">
                {Object.entries(errors).map(([field, msg]) => (
                  <li key={field}>
                    {field.replace('user.', '')}: {msg}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.email}
                required
                className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Password */}
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.password}
                required
                className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              </span>
              <input
                type="password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                placeholder={t.confirmPassword}
                required
                className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Subdomain */}
            <div className="flex items-center" dir="ltr">
              <span className="flex h-[46px] items-center rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-400">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                  />
                </svg>
              </span>
              <input
                type="text"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
                placeholder={t.subdomain}
                required
                className="flex-1 border border-gray-300 py-3 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="flex h-[46px] items-center rounded-r-lg border border-l-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500">
                {domainSuffix}
              </span>
            </div>
            {errors.subdomain_name && (
              <p className="text-xs text-red-500">{errors.subdomain_name}</p>
            )}

            {/* Submit */}
            <div className="pt-2 text-center">
              <button
                type="submit"
                disabled={processing}
                className="w-1/2 rounded-full py-3 text-sm font-semibold text-white transition-colors disabled:opacity-60"
                style={{ backgroundColor: '#252854' }}
              >
                {processing ? '...' : t.createAccountBtn}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
